import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Paper,
  Avatar,
  Divider,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  PhotoCamera, 
  Send as SendIcon,
  Person as PersonIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  ContentCopy as DuplicateIcon,
  DeleteOutline as DeleteIcon
} from '@mui/icons-material';
// Removed date picker imports to avoid LocalizationProvider issues
import api, { getBackendURL } from '../api';
import { getFullPhotoUrl, withCacheBuster } from '../utils/photoUrl';
import { compressImage } from '../utils/imageCompression';

const TaskDialog = ({ 
  open, 
  onClose, 
  task, 
  objectId, 
  userList,
  onTaskSave 
}) => {
  const isEditing = !!task;
  
  // Mobile responsive breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Form states
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState(null);
  const [assignedTo, setAssignedTo] = useState('');
  const [status, setStatus] = useState('offen');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  
  // Comment states
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  
  // Sub-task states
  const [subTasks, setSubTasks] = useState([]);
  const [subTasksLoading, setSubTasksLoading] = useState(false);
  const [newSubTaskTitle, setNewSubTaskTitle] = useState('');
  const [newSubTaskAssignedTo, setNewSubTaskAssignedTo] = useState('');
  const [newSubTaskDueDate, setNewSubTaskDueDate] = useState('');
  const [addingSubTask, setAddingSubTask] = useState(false);
  // Sub-task three-dot menu
  const [subTaskMenuAnchor, setSubTaskMenuAnchor] = useState(null);
  const [subTaskMenuTarget, setSubTaskMenuTarget] = useState(null);
  // Inline edit for sub-tasks
  const [editingSubTaskId, setEditingSubTaskId] = useState(null);
  const [editingSubTaskTitle, setEditingSubTaskTitle] = useState('');
  const [editingSubTaskAssignedTo, setEditingSubTaskAssignedTo] = useState('');
  const [editingSubTaskDueDate, setEditingSubTaskDueDate] = useState('');

  // Loading states
  const [loading, setLoading] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);

  // Initialize form when task changes or dialog opens
  useEffect(() => {
    if (!open) return;
    if (task) {
      setTitle(task.title || '');
      setDescription(task.description || '');
      setDueDate(task.dueDate ? new Date(task.dueDate) : null);
      setAssignedTo(task.assignedTo?._id || task.assignedTo || '');
      setStatus(task.status || 'offen');
      setComments(task.comments || []);
      if (task.photo) {
        // Use task's updatedAt or current time as cache buster
        const timestamp = task.updatedAt ? new Date(task.updatedAt).getTime() : '';
        const photoUrl = withCacheBuster(getFullPhotoUrl(task.photoUrl || task.photo), timestamp);
        console.log('Setting photo URL:', photoUrl, 'for task photo:', task.photo);
        setPhotoPreview(photoUrl);
      } else {
        console.log('No photo found for task:', task._id);
        setPhotoPreview('');
      }
    } else {
      // Reset form for new task
      setTitle('');
      setDescription('');
      setDueDate(null);
      setAssignedTo('');
      setStatus('offen');
      setPhoto(null);
      setPhotoPreview('');
      setComments([]);
    }
    setNewComment('');
  }, [task, open]);

  // Load sub-tasks whenever the parent task changes (edit mode only)
  useEffect(() => {
    if (!open || !task?._id) {
      setSubTasks([]);
      return;
    }
    setSubTasksLoading(true);
    api.get('/tasks', { params: { parentTask: task._id } })
      .then(res => setSubTasks(res.data))
      .catch(err => { console.error('Error loading sub-tasks:', err); setSubTasks([]); })
      .finally(() => setSubTasksLoading(false));
  }, [open, task?._id]);

  const handleClose = () => {
    // Reset photo states
    setPhoto(null);
    setPhotoPreview('');
    setSubTasks([]);
    setNewSubTaskTitle('');
    setNewSubTaskAssignedTo('');
    setNewSubTaskDueDate('');
    onClose();
  };

  const handlePhotoChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      let compressed = file;
      try { compressed = new File([await compressImage(file)], file.name, { type: 'image/jpeg' }); } catch { /* use original */ }
      setPhoto(compressed);
      const reader = new FileReader();
      reader.onload = (e) => setPhotoPreview(e.target.result);
      reader.readAsDataURL(compressed);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      alert('Titel ist erforderlich');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('status', status);
      
      if (dueDate) {
        formData.append('dueDate', dueDate.toISOString());
      }
      if (assignedTo) {
        formData.append('assignedTo', assignedTo);
      }
      if (photo) {
        formData.append('photo', photo);
      }

      let response;
      if (isEditing) {
        response = await api.put(`/tasks/${task._id}`, formData);
      } else {
        formData.append('object', objectId);
        response = await api.post(`/objects/${objectId}/tasks`, formData);
      }

      onTaskSave(response.data);
      handleClose();
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Fehler beim Speichern der Aufgabe');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubTask = async () => {
    if (!newSubTaskTitle.trim()) return;
    setAddingSubTask(true);
    try {
      const response = await api.post('/tasks', {
        title: newSubTaskTitle.trim(),
        assignedTo: newSubTaskAssignedTo || undefined,
        dueDate: newSubTaskDueDate || undefined,
        parentTask: task._id,
        // object is inherited server-side from parentTask
      });
      setSubTasks(prev => [...prev, response.data]);
      setNewSubTaskTitle('');
      setNewSubTaskAssignedTo('');
      setNewSubTaskDueDate('');
    } catch (error) {
      console.error('Error creating sub-task:', error);
      alert('Fehler beim Erstellen der Sub-Aufgabe');
    } finally {
      setAddingSubTask(false);
    }
  };

  const handleToggleSubTask = async (subTask) => {
    const newStatus = subTask.status === 'erledigt' ? 'offen' : 'erledigt';
    try {
      await api.put(`/tasks/${subTask._id}`, { status: newStatus });
      setSubTasks(prev => prev.map(t =>
        t._id === subTask._id ? { ...t, status: newStatus } : t
      ));
    } catch (error) {
      console.error('Error toggling sub-task:', error);
    }
  };

  const handleDeleteSubTask = async (subTask) => {
    try {
      await api.delete(`/tasks/${subTask._id}`);
      setSubTasks(prev => prev.filter(t => t._id !== subTask._id));
    } catch (error) {
      console.error('Error deleting sub-task:', error);
    }
  };

  const handleDuplicateSubTask = async (subTask) => {
    try {
      const response = await api.post('/tasks', {
        title: `${subTask.title} (Kopie)`,
        assignedTo: subTask.assignedTo?._id || subTask.assignedTo || undefined,
        dueDate: subTask.dueDate || undefined,
        parentTask: task._id,
      });
      setSubTasks(prev => [...prev, response.data]);
    } catch (error) {
      console.error('Error duplicating sub-task:', error);
    }
  };

  const handleStartEditSubTask = (subTask) => {
    setEditingSubTaskId(subTask._id);
    setEditingSubTaskTitle(subTask.title);
    setEditingSubTaskAssignedTo(subTask.assignedTo?._id || subTask.assignedTo || '');
    setEditingSubTaskDueDate(subTask.dueDate ? new Date(subTask.dueDate).toISOString().split('T')[0] : '');
  };

  const handleSaveEditSubTask = async (subTaskId) => {
    try {
      const response = await api.put(`/tasks/${subTaskId}`, {
        title: editingSubTaskTitle,
        assignedTo: editingSubTaskAssignedTo || undefined,
        dueDate: editingSubTaskDueDate || undefined,
      });
      setSubTasks(prev => prev.map(t => t._id === subTaskId ? response.data : t));
      setEditingSubTaskId(null);
    } catch (error) {
      console.error('Error saving sub-task edit:', error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setCommentsLoading(true);
    try {
      const response = await api.post(`/tasks/${task._id}/comments`, {
        text: newComment
      });
      
      setComments([...comments, response.data]);
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Fehler beim Hinzufügen des Kommentars');
    } finally {
      setCommentsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'offen': return '#gray';
      case 'gestartet': return '#1976d2';
      case 'erledigt': return '#2e7d32';
      case 'problem': return '#d32f2f';
      default: return '#gray';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'offen': return 'Offen';
      case 'gestartet': return 'Gestartet';
      case 'erledigt': return 'Erledigt';
      case 'problem': return 'Problem';
      default: return status;
    }
  };

  return (
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth={isMobile ? false : "md"}
        fullWidth={!isMobile}
        fullScreen={isMobile}
        PaperProps={{ 
          sx: { 
            minHeight: { xs: '100vh', sm: '60vh' },
            margin: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: 2 }
          } 
        }}
      >
        <DialogTitle>
          {isEditing ? 'Aufgabe bearbeiten' : 'Neue Aufgabe erstellen'}
        </DialogTitle>
        
        <DialogContent dividers sx={{ overflow: 'auto', flex: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Hauptformular */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Titel - Required */}
              <TextField
                label="Titel *"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
                error={!title.trim()}
                helperText={!title.trim() ? 'Titel ist erforderlich' : ''}
              />

              {/* Beschreibung */}
              <TextField
                label="Beschreibung"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
              />

              {/* Fälligkeitsdatum */}
              <TextField
                fullWidth
                label="Fälligkeitsdatum"
                type="date"
                value={dueDate ? dueDate.toISOString().split('T')[0] : ''}
                onChange={(e) => setDueDate(e.target.value ? new Date(e.target.value) : null)}
                InputLabelProps={{
                  shrink: true,
                }}
              />

              {/* Zugewiesen an */}
              <FormControl fullWidth>
                <InputLabel>Zugewiesen an</InputLabel>
                <Select
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  label="Zugewiesen an"
                >
                  <MenuItem value="">
                    <em>Nicht zugewiesen</em>
                  </MenuItem>
                  {userList?.map((user) => (
                    <MenuItem key={user._id} value={user._id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" />
                        {user.firstName && user.lastName 
                          ? `${user.firstName} ${user.lastName}` 
                          : user.username
                        }
                      </Box>
                    </MenuItem>
                  )) || []}
                </Select>
              </FormControl>

              {/* Status */}
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  label="Status"
                >
                  {['offen', 'gestartet', 'erledigt', 'problem'].map((statusOption) => (
                    <MenuItem key={statusOption} value={statusOption}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: getStatusColor(statusOption)
                          }}
                        />
                        {getStatusLabel(statusOption)}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Foto Upload */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Foto
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="photo-upload"
                    type="file"
                    onChange={handlePhotoChange}
                  />
                  <label htmlFor="photo-upload">
                    <IconButton color="primary" aria-label="upload picture" component="span">
                      <PhotoCamera />
                    </IconButton>
                  </label>
                  {photoPreview && (
                    <Box
                      component="img"
                      src={photoPreview}
                      alt="Task photo"
                      sx={{
                        width: 100,
                        height: 100,
                        objectFit: 'cover',
                        borderRadius: 1,
                        border: '1px solid #ddd'
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            {/* Sub-Aufgaben - nur im Edit-Modus */}
            {isEditing && (
              <>
                <Divider />
                <Box>
                  {/* Header with progress */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Typography variant="h6" sx={{ flex: 1 }}>
                      Sub-Aufgaben
                    </Typography>
                    {subTasksLoading ? (
                      <CircularProgress size={20} />
                    ) : subTasks.length > 0 ? (
                      <Chip
                        size="small"
                        label={`${subTasks.filter(t => t.status === 'erledigt').length}/${subTasks.length}`}
                        color={subTasks.every(t => t.status === 'erledigt') ? 'success' : 'primary'}
                        variant="outlined"
                      />
                    ) : null}
                  </Box>

                  {/* Sub-task list */}
                  <Box sx={{ mb: 1.5 }}>
                    {subTasks.map(subTask => {
                      const isDone = subTask.status === 'erledigt';
                      const assigneeName = subTask.assignedTo
                        ? (subTask.assignedTo.firstName && subTask.assignedTo.lastName
                          ? `${subTask.assignedTo.firstName} ${subTask.assignedTo.lastName}`
                          : subTask.assignedTo.username || '')
                        : null;
                      const isSubOverdue = !isDone && subTask.dueDate && new Date(subTask.dueDate) < new Date();
                      const isEditingThis = editingSubTaskId === subTask._id;

                      return (
                        <Box
                          key={subTask._id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.75,
                            px: 1,
                            mb: 0.5,
                            borderRadius: 1,
                            bgcolor: isDone ? 'action.hover' : 'background.paper',
                            border: '1px solid',
                            borderColor: isDone ? 'divider' : isSubOverdue ? 'error.light' : 'divider',
                            opacity: isDone ? 0.65 : 1,
                            flexWrap: isEditingThis ? 'wrap' : 'nowrap'
                          }}
                        >
                          {isEditingThis ? (
                            /* Inline edit form */
                            <>
                              <TextField
                                value={editingSubTaskTitle}
                                onChange={e => setEditingSubTaskTitle(e.target.value)}
                                size="small"
                                sx={{ flex: '1 1 140px', minWidth: 100 }}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSaveEditSubTask(subTask._id); } if (e.key === 'Escape') setEditingSubTaskId(null); }}
                                autoFocus
                              />
                              <FormControl size="small" sx={{ flex: '0 1 130px', minWidth: 90 }}>
                                <InputLabel>Zugewiesen</InputLabel>
                                <Select value={editingSubTaskAssignedTo} onChange={e => setEditingSubTaskAssignedTo(e.target.value)} label="Zugewiesen">
                                  <MenuItem value=""><em>–</em></MenuItem>
                                  {(userList || []).map(u => (
                                    <MenuItem key={u._id} value={u._id}>
                                      {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.username}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <TextField
                                type="date"
                                size="small"
                                value={editingSubTaskDueDate}
                                onChange={e => setEditingSubTaskDueDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                label="Datum"
                                sx={{ flex: '0 1 130px', minWidth: 110 }}
                              />
                              <Button size="small" variant="contained" onClick={() => handleSaveEditSubTask(subTask._id)} sx={{ minHeight: 36 }}>OK</Button>
                              <Button size="small" onClick={() => setEditingSubTaskId(null)} sx={{ minHeight: 36 }}>✕</Button>
                            </>
                          ) : (
                            /* Normal display */
                            <>
                              <Tooltip title={isDone ? 'Als offen markieren' : 'Als erledigt markieren'}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleSubTask(subTask)}
                                  sx={{
                                    color: isDone ? 'success.main' : 'action.disabled',
                                    minWidth: { xs: 40, sm: 32 },
                                    minHeight: { xs: 40, sm: 32 },
                                    p: 0.5
                                  }}
                                >
                                  {isDone ? <CheckCircleIcon fontSize="small" /> : <UncheckedIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                              <Typography
                                variant="body2"
                                sx={{
                                  flex: 1,
                                  textDecoration: isDone ? 'line-through' : 'none',
                                  color: isSubOverdue ? 'error.main' : 'text.primary'
                                }}
                              >
                                {subTask.title}
                              </Typography>
                              {assigneeName && (
                                <Chip
                                  avatar={<Avatar sx={{ width: 18, height: 18 }}>{assigneeName[0]}</Avatar>}
                                  label={assigneeName}
                                  size="small"
                                  variant="outlined"
                                  sx={{ maxWidth: 120, fontSize: '0.7rem' }}
                                />
                              )}
                              {subTask.dueDate && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: isSubOverdue ? 'error.main' : 'text.secondary', whiteSpace: 'nowrap' }}
                                >
                                  {new Date(subTask.dueDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                                </Typography>
                              )}
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); setSubTaskMenuAnchor(e.currentTarget); setSubTaskMenuTarget(subTask); }}
                                sx={{ p: 0.5, minWidth: { xs: 36, sm: 28 }, minHeight: { xs: 36, sm: 28 } }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      );
                    })}
                    {!subTasksLoading && subTasks.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 1, px: 1 }}>
                        Noch keine Sub-Aufgaben
                      </Typography>
                    )}
                  </Box>

                  {/* Quick-add new sub-task */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-end', p: 1, bgcolor: '#f9f9f9', borderRadius: 1, border: '1px dashed #ddd' }}>
                    <TextField
                      placeholder="Sub-Aufgabe hinzufügen..."
                      value={newSubTaskTitle}
                      onChange={e => setNewSubTaskTitle(e.target.value)}
                      size="small"
                      sx={{ flex: '1 1 160px', minWidth: 120 }}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddSubTask(); } }}
                    />
                    <FormControl size="small" sx={{ flex: '0 1 140px', minWidth: 100 }}>
                      <InputLabel>Zugewiesen</InputLabel>
                      <Select
                        value={newSubTaskAssignedTo}
                        onChange={e => setNewSubTaskAssignedTo(e.target.value)}
                        label="Zugewiesen"
                      >
                        <MenuItem value=""><em>–</em></MenuItem>
                        {(userList || []).map(u => (
                          <MenuItem key={u._id} value={u._id}>
                            {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.username}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      type="date"
                      size="small"
                      value={newSubTaskDueDate}
                      onChange={e => setNewSubTaskDueDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      label="Datum"
                      sx={{ flex: '0 1 140px', minWidth: 120 }}
                    />
                    <IconButton
                      color="primary"
                      onClick={handleAddSubTask}
                      disabled={!newSubTaskTitle.trim() || addingSubTask}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        minWidth: { xs: 44, sm: 36 },
                        minHeight: { xs: 44, sm: 36 },
                        '&:hover': { bgcolor: 'primary.dark' },
                        '&:disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' }
                      }}
                    >
                      {addingSubTask ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    </IconButton>
                  </Box>
                </Box>
              </>
            )}

            {/* Kommentare - nur im Edit-Modus */}
            {isEditing && (
              <>
                <Divider />
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Kommentare
                  </Typography>
                  
                  {/* Existing Comments */}
                  <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}>
                    {comments.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        Noch keine Kommentare vorhanden
                      </Typography>
                    ) : (
                      comments.map((comment, index) => (
                        <Paper key={index} sx={{ p: 2, mb: 1, bgcolor: '#f5f5f5' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Avatar sx={{ width: 24, height: 24 }}>
                              {comment.user?.firstName?.[0] || comment.user?.username?.[0] || '?'}
                            </Avatar>
                            <Typography variant="subtitle2">
                              {comment.user?.firstName && comment.user?.lastName
                                ? `${comment.user.firstName} ${comment.user.lastName}`
                                : comment.user?.username || 'Unbekannt'
                              }
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(comment.createdAt).toLocaleString('de-DE')}
                            </Typography>
                          </Box>
                          <Typography variant="body2">
                            {comment.text}
                          </Typography>
                        </Paper>
                      ))
                    )}
                  </Box>

                  {/* Add New Comment */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      label="Neuer Kommentar"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      fullWidth
                      multiline
                      rows={2}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleAddComment();
                        }
                      }}
                    />
                    <IconButton
                      color="primary"
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || commentsLoading}
                      size={isMobile ? "large" : "medium"}
                      sx={{ 
                        alignSelf: 'flex-end', 
                        mb: 1,
                        minWidth: { xs: 48, sm: 'auto' },
                        minHeight: { xs: 48, sm: 'auto' }
                      }}
                    >
                      <SendIcon />
                    </IconButton>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: { xs: 2, sm: 1 }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={handleClose}
            size={isMobile ? "large" : "medium"}
            sx={{ 
              minHeight: { xs: 48, sm: 'auto' },
              flex: { xs: 1, sm: 'none' }
            }}
          >
            Abbrechen
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={loading || !title.trim()}
            size={isMobile ? "large" : "medium"}
            sx={{ 
              minHeight: { xs: 48, sm: 'auto' },
              flex: { xs: 1, sm: 'none' }
            }}
          >
            {loading ? 'Speichern...' : (isEditing ? 'Aktualisieren' : 'Erstellen')}
          </Button>
        </DialogActions>

        {/* Sub-task three-dot menu */}
        <Menu
          anchorEl={subTaskMenuAnchor}
          open={Boolean(subTaskMenuAnchor)}
          onClose={() => { setSubTaskMenuAnchor(null); setSubTaskMenuTarget(null); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => {
            handleStartEditSubTask(subTaskMenuTarget);
            setSubTaskMenuAnchor(null);
            setSubTaskMenuTarget(null);
          }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Bearbeiten</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            handleDuplicateSubTask(subTaskMenuTarget);
            setSubTaskMenuAnchor(null);
            setSubTaskMenuTarget(null);
          }}>
            <ListItemIcon><DuplicateIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Duplizieren</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => {
            handleDeleteSubTask(subTaskMenuTarget);
            setSubTaskMenuAnchor(null);
            setSubTaskMenuTarget(null);
          }} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Löschen</ListItemText>
          </MenuItem>
        </Menu>
      </Dialog>
  );
};

export default TaskDialog;